import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { GateComponent } from "../../../models/api/gateComponents/gateComponent";
import { PredictiveService } from "../../../models/api/gateComponents/predictiveService";
import {
  PredictiveServiceHistoryObject,
  PredictiveServiceConfirmation,
  PredictiveServiceHistory,
} from "../../../models/api/gateComponents/predictiveServiceHistory";

@Injectable({
  providedIn: "root",
})
export class GateComponentsService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);

  /**
   * Sends a POST request to create a new gate component.
   * @param gateComponent The gate component to be created.
   * @returns An Observable of the response.
   */
  public createGateComponent(gateComponent: GateComponent) {
    return this.http
      .post(environment.baseUrl + `/gateComponent`, gateComponent)
      .pipe(this.apiErrorHandler.handle("gate_component"));
  }

  /**
   * Sends a PUT request to edit an existing gate component.
   * @param gateComponent The gate component to be edited.
   * @returns An Observable of the response.
   */
  public editGateComponent(gateComponent: GateComponent) {
    const gateComponentId = gateComponent.id;
    delete gateComponent.id;
    return this.http
      .put(environment.baseUrl + `/gateComponent/${gateComponentId}`, gateComponent)
      .pipe(this.apiErrorHandler.handle("gate_component"));
  }

  /**
   * Loads all gate components from the server and converts them to predictive services.
   * @returns An Observable of the predictive services.
   */
  public getPredictiveServices() {
    return this.http.get<GateComponent[]>(environment.baseUrl + `/gateComponent`).pipe(
      this.apiErrorHandler.handle("gate_component"),
      map((res) => {
        return this.convertToPredictiveServices(res);
      })
    );
  }

  /**
   * Sends a PUT request to edit an existing predictive service.
   * @param predictiveServiceId The id of the predictive service to be edited.
   * @param predictiveServiceEditValues The values to edit the predictive service with.
   * @returns An Observable of the predictive service confirmation.
   */
  public editPredictiveService(
    predictiveServiceId: string,
    predictiveServiceEditValues: PredictiveServiceHistoryObject
  ) {
    return this.http
      .put<PredictiveServiceConfirmation>(
        environment.baseUrl + `/predictiveService/${predictiveServiceId}`,
        predictiveServiceEditValues
      )
      .pipe(this.apiErrorHandler.handle("gate_component"));
  }

  /**
   * Converts an array of GateComponents to an array of PredictiveServices.
   * @param gateComponents The gate components to convert.
   * @returns An array of PredictiveServices.
   */
  public convertToPredictiveServices(gateComponents: GateComponent[]) {
    return gateComponents.map((gateComponent) => new PredictiveService(gateComponent));
  }

  /**
   * Deletes a gate component with the given id.
   * @param gateComponentId The id of the gate component to be deleted.
   * @returns An Observable of the response.
   */
  public deleteGateComponent(gateComponentId: string | number) {
    return this.http
      .delete(environment.baseUrl + `/gateComponent/${gateComponentId}`)
      .pipe(this.apiErrorHandler.handle("gate_component"));
  }

  /**
   * Gets all gate components of a gate system.
   * @param gateSystemId The id of the gate system to get the gate components for.
   * @returns An Observable of an array of gate components, or null if no gate components are found.
   */
  public getGateComponents(gateSystemId: string): Observable<GateComponent[] | null> {
    return this.http
      .get<GateComponent[]>(environment.baseUrl + `/gateComponent`, {
        params: {
          gateSystemId,
        },
      })
      .pipe(this.apiErrorHandler.handle("gate_component"));
  }

  /**
   * Gets the predictive service history for a given gate component.
   * @param gateComponentId The id of the gate component to get the predictive service history for.
   * @returns An Observable of an array of PredictiveServiceHistory objects.
   */
  public getPredictiveServiceHistory(gateComponentId: string) {
    return this.http
      .get<PredictiveServiceHistoryObject[]>(`${environment.baseUrl}/predictiveService`, {
        params: { gateComponentId },
      })
      .pipe(
        this.apiErrorHandler.handle("predictive_service"),
        map((res) => {
          return res.map((predictiveService) => {
            return new PredictiveServiceHistory(predictiveService);
          });
        })
      );
  }
}
